import { Component, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AssetBasketService, DownloadAssetsService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetUIModel,
  ClientEnvironment,
  CommonUtilityLib,
  ENVIRONMENT,
  FilterAllOptions,
  FilterFormDataModel,
  ResizeService,
  TagsDataConfig,
  ViewType,
  accessAsset,
  anyAsset,
} from '@thehubz/client/core';
import { Asset, TagsWithAssetCount } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata } from '@thehubz/shared/model';
import { Subject, Subscription, filter } from 'rxjs';
import { delay, startWith, tap } from 'rxjs/operators';
import { SeatTableColumnData } from '../../../../customer/seat/seat-table-column-data';
import { VorwerkTableColumnData } from '../../../../customer/vorwerk/vorwerk-table-column-data';
import { AssetCommonService } from '../../../../services/asset-common.service';
import { AssetViewTypeService, GridConfig } from '../../../../services/asset-view-type.service';
import { ProductBrandSelectionService } from '../../../../services/product-brand-selection.service';
import { AssetDownloadComponent, AssetDownloadData } from '../../../asset-download/asset-download.component';
import { AssetEditPopupComponent } from '../../../asset-edit-popup/asset-edit-popup.component';
import { AssetGalleryViewComponent } from '../../../asset-gallery-view/asset-gallery-view.component';
import { AssetsSidebarComponent } from '../../../assets/assets-sidebar/assets-sidebar.component';
import { TagsDialogComponent } from '../../../tags-dialog/tags-dialog.component';
import { CollectionEventsService } from '../../services/collection-events.service';
import { CollectionSelectionService } from '../../services/collection-selection.service';
import { CollectionAssetCommonService } from '../collection-assets-common.service';
import { TableColumnData } from './table-column-data';

@Component({
  selector: 'thehubz-thumbnail-list',
  templateUrl: './thumbnail-list.component.html',
  styleUrls: ['./thumbnail-list.component.scss'],
})
export class ThumbnailListComponent implements OnInit, OnDestroy {
  @ViewChild(AssetsSidebarComponent) assetSidebar?: AssetsSidebarComponent;
  @ViewChild(AssetGalleryViewComponent) public assetGalleryView?: AssetGalleryViewComponent;
  @Input() showFilterBubbles = true;
  @Input() gridSettingsKeySuffix = 'CollectionOverview';
  @Input() selectedViewType!: ViewType;

  brandingKey!: CustomClients;

  filteredList: AssetUIModel[] = [];
  filteredSplitList!: AssetUIModel[][];
  filterBubbles: { _id: string; name: string }[] = [];
  collectionId!: string;
  loading = false;
  httpCallInProgress = false;
  subscriptions?: Subscription;
  collectionFlag = true;
  appliedFilters = 0;
  showEmptyState = false;

  chunkedList: AssetUIModel[] = [];
  chunkedSplitList!: AssetUIModel[][];
  chunkSize = 100;

  AssetType = Asset.AssetTypeEnum;
  AccessAsset = accessAsset;
  AnyAsset = anyAsset;
  isOwner = false;

  selectedViewSection2: ViewType = 'sortByLastCreated';
  filterCollectionFormData!: FilterFormDataModel;
  selectedFilterObject: AnyType;
  selectedTabKey = 'collectionListView';

  /* Use formResetter when filter bubble is cleared */
  formResetter = new Subject<{ _id: string; name: string; key: string } | undefined>();

  currentSize: AnyType;
  prefix = 'is-';
  size = 4;
  sizes = AppSettings.SCREEN_SIZES;
  itemSize = 310;
  offset = 0;

  assetCount = 0;
  dialogEditState?: number;

  gridConfig: GridConfig = {
    showListView: false,
    showGridView: true,
    showGridViewSmall: true,
    showGridViewLarge: false,
    showGridViewSidebar: false,
    gridSize: 4,
  };

  filterObject!: FilterAllOptions;

  configObject = {
    callFrom: 'collection',
    hideDelete: true,
    hideAddToBasket: true,
    hideAddToCollection: true,
  };

  tagsObj = {
    tags: [],
  };

  dialogEditRef!: MatDialogRef<AssetEditPopupComponent>;
  tagsDialogRef!: MatDialogRef<TagsDialogComponent, TagsWithAssetCount[]>;

  tableColumnData = TableColumnData;
  tableData: AnyType;

  private resizeSubscription?: Subscription;

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private router: Router,
    private dialog: MatDialog,
    private elementRef: ElementRef,
    private resizeSvc: ResizeService,
    private commonUtilityLib: CommonUtilityLib,
    private assetBasketService: AssetBasketService,
    private assetCommonService: AssetCommonService,
    protected assetViewTypeService: AssetViewTypeService,
    private downloadAssetsService: DownloadAssetsService,
    protected collectionEventsService: CollectionEventsService,
    private collectionSelectionService: CollectionSelectionService,
    private collectionAssetCommonService: CollectionAssetCommonService,
    private productBrandSelectionService: ProductBrandSelectionService,
  ) {
    this.brandingKey = this.env.brandingKey;
    this.resizeSubscription = this.resizeSvc.onResize$.pipe(delay(0)).subscribe((x) => {
      const currentSize = this.sizes.find((x) => {
        const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
        const isVisible = window.getComputedStyle(el).display != 'none';
        return isVisible;
      });
      this.currentSize = currentSize;
      this.setAssetView(currentSize);
    });

    if (this.brandingKey === CustomClients.Vorwerk) {
      this.tableColumnData = VorwerkTableColumnData;
    }
    if (this.brandingKey === CustomClients.SEAT) {
      this.tableColumnData = SeatTableColumnData;
    }
    if (this.brandingKey === CustomClients.LufthansaMediacenter || this.brandingKey === CustomClients.LufthansaMedialounge) {
      const idx = this.tableColumnData.TABLE_DEFAULT_COLUMNS.findIndex((columns) => columns.columnDef === 'expiryDate');
      if (idx != -1) {
        this.tableColumnData.TABLE_DEFAULT_COLUMNS.splice(idx, 1);
      }
    }
  }

  @HostListener('window:resize', [])
  private onResize() {
    this.detectScreenSize();
  }

  ngOnInit() {
    this.subscriptions = new Subscription();
    const currentPath = this.router.url.split('/');
    this.collectionId = currentPath[currentPath.length - 2];
    this.assetBasketService.useCollectionBasket(this.collectionId);
    this.httpCallInProgress = true;
    this.size = this.assetViewTypeService.getGridSettings('gridSize', this.gridSettingsKeySuffix);
    const collectionAssetDataSubs = this.collectionEventsService.collectionAssetDataSubject$
      .pipe(
        startWith([]), // This ensures the initial loading state is handled
        tap((data) => {
          if (Array.isArray(data) && data.length > 0) {
            this.httpCallInProgress = false;
          } else {
            // The condition to handle the case when there is no data
            setTimeout(() => {
              this.httpCallInProgress = false;
            }, 2000); // Delay to ensure the loading component is shown briefly
          }
        }),
      )
      .subscribe((data) => {
        if (Array.isArray(data)) {
          this.filteredList = data;
        } else {
          this.filteredList = [];
        }
        this.filteredList.forEach((asset: AssetUIModel) => {
          asset['assetExpiryDays'] = this.assetCommonService.getAssetExpireDays(asset.expiryDate);
        });

        this.createSplitList();

        this.showEmptyState = !this.filteredSplitList || this.filteredSplitList.length === 0;
      });
    this.subscriptions.add(collectionAssetDataSubs);

    const collectionFilterDataSubs = this.collectionEventsService.collectionFilterDataSubject$.subscribe((data) => (this.filterCollectionFormData = data));
    this.subscriptions.add(collectionFilterDataSubs);

    const selectedFilterDataSubs = this.assetCommonService.selectedFilterDataSubject$.subscribe((data) => {
      if (Object.keys(data).length) {
        this.selectedFilterObject = data.selectedFilterObject;
        this.filterObject = data.filterObject;
      }
    });
    this.subscriptions.add(selectedFilterDataSubs);

    const filterBubblesSubs = this.collectionEventsService.collectionFilterBubblesSubject$.subscribe((data) => (this.filterBubbles = data));
    this.subscriptions.add(filterBubblesSubs);

    const splitCollectionAssetsSubs = this.collectionEventsService.splitCollectionAssets.subscribe((size) => {
      if (size) {
        this.size = size;
        this.createSplitList();
      }
    });
    this.subscriptions.add(splitCollectionAssetsSubs);

    const collectionUpdateSidebarSubs = this.collectionEventsService.collectionUpdateSidebar$.subscribe((data) => {
      if (data.formControlName === 'brand') {
        this.productBrandSelectionService.updateOnBrandSelection(
          data.fieldData,
          this.assetSidebar?.filterForms.filterForm as AbstractControl<AnyType, AnyType>,
        );
      } else if (data.formControlName === 'product') {
        this.productBrandSelectionService.updateOnProductSelection(
          data.fieldData,
          this.assetSidebar?.filterForms.filterForm as AbstractControl<AnyType, AnyType>,
        );
      } else {
        this.assetSidebar?.filterForms.resetFormData();
      }
    });
    this.subscriptions.add(collectionUpdateSidebarSubs);

    // get collection data observable if collection object is not empty
    const collectionDataSubject$ = this.collectionEventsService.collectionDataSubject$
      .pipe(filter((coll) => !this.commonUtilityLib.isEmptyObject(coll)))
      .subscribe((collectionInfo) => {
        this.isOwner = this.assetCommonService.checkCollectionOwner(collectionInfo);
      });

    this.subscriptions.add(collectionDataSubject$);

    const collectionCountSubject$ = this.collectionEventsService.collectionAssetCountSubject$.subscribe((count: number) => {
      this.assetCount = count;
    });
    this.subscriptions.add(collectionCountSubject$);

    const collectionsReloadOffsetSubject$ = this.collectionEventsService.collectionsReloadOffsetSubject$.subscribe((value: number) => {
      this.offset = value;
    });
    this.subscriptions.add(collectionsReloadOffsetSubject$);
  }

  removeFilterBubble(data: AnyType) {
    if (this.filterBubbles.length === 1 && data._id === this.filterBubbles[0]._id) {
      this.removeAllFilterBubble();
      return;
    }

    this.collectionEventsService.removeCollectionFilterBubble.next(data);
    this.formResetter.next(data);
  }

  scrollGalleryToIndex(index: number, behavior: ScrollBehavior = 'instant') {
    if (this.assetGalleryView && typeof this.assetGalleryView.scrollToIndex === 'function') {
      this.assetGalleryView.scrollToIndex(index, behavior);
    }
  }

  removeAllFilterBubble() {
    this.collectionEventsService.removeAllCollectionFilterBubble.next(true);
    this.formResetter.next(undefined);
  }

  onAssetScroll(scroll: { offset: number; direction?: string; anchorIndex?: number }) {
    this.offset = scroll.offset;
    this.collectionEventsService.collectionsReloadCounterSubject$.next(scroll);
  }

  setAssetView(currentSize?: AnyType) {
    const gridSettings = this.assetViewTypeService.getGridConfig(this.gridSettingsKeySuffix);
    if (gridSettings.showGridViewLarge && !gridSettings.showGridViewSidebar) {
      this.size = 8;
      if (currentSize?.name === 'xl') this.size = 8;
      if (currentSize?.name === 'lg') this.size = 8;
      if (currentSize?.name === 'md') this.size = 4;
    }
    if (gridSettings.showGridViewSmall && !gridSettings.showGridViewSidebar) {
      this.size = 4;
      if (currentSize?.name === 'lg') this.size = 3;
      if (currentSize?.name === 'md') this.size = 2;
    }
    if (gridSettings.showGridViewSidebar && gridSettings.showGridViewSmall) {
      this.size = 3;
      if (currentSize?.name === 'xl') this.size = 3;
      if (currentSize?.name === 'lg') this.size = 2;
      if (currentSize?.name === 'md') this.size = 1;
    }
    if (gridSettings.showGridViewSidebar && gridSettings.showGridViewLarge) {
      this.size = 6;
      if (currentSize?.name === 'xl') this.size = 6;
      if (currentSize?.name === 'lg') this.size = 6;
      if (currentSize?.name === 'md') this.size = 4;
    }
  }

  changeAssetView(view: AnyType) {
    if (view !== 'list') {
      this.gridConfig.showListView = false;
      this.gridConfig.showGridView = true;

      if (view === 'grid_4') {
        this.gridConfig.showGridViewSmall = true;
        this.gridConfig.showGridViewLarge = false;
        this.gridConfig.showGridViewSidebar = false;
        this.gridConfig.gridSize = 4;
        this.selectedViewType = 'grid_4';
        this.size = 4;
      }
      if (view === 'grid_8') {
        this.gridConfig.showGridViewSmall = false;
        this.gridConfig.showGridViewLarge = true;
        this.gridConfig.showGridViewSidebar = false;
        this.gridConfig.gridSize = 8;
        this.selectedViewType = 'grid_8';
        this.size = 8;
        this.itemSize = 155;
      }
      if (view === 'grid_3') {
        this.gridConfig.showGridViewSmall = true;
        this.gridConfig.showGridViewLarge = false;
        this.gridConfig.showGridViewSidebar = true;
        this.gridConfig.gridSize = 3;
        this.selectedViewType = 'grid_3';
        this.size = 3;
      }
      if (view === 'grid_6') {
        this.gridConfig.showGridViewSmall = false;
        this.gridConfig.showGridViewLarge = true;
        this.gridConfig.showGridViewSidebar = true;
        this.gridConfig.gridSize = 6;
        this.selectedViewType = 'grid_6';
        this.size = 6;
      }
    } else {
      this.gridConfig.showGridView = false;
      this.gridConfig.showGridViewSmall = false;
      this.gridConfig.showGridViewLarge = false;
      this.gridConfig.showListView = true;
      this.selectedViewType = 'list';
    }

    this.assetViewTypeService.setGridConfig(this.gridSettingsKeySuffix, this.gridConfig);
  }

  private split(input: AssetUIModel[], groupSize: number): AssetUIModel[][] {
    const out = [];
    for (let i = 0; i < input.length; i += groupSize) {
      out.push(input.slice(i, i + groupSize));
    }
    return out;
  }

  private detectScreenSize() {
    const currentSize = this.sizes.find((x) => {
      const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
      const isVisible = window.getComputedStyle(el).display != 'none';
      return isVisible;
    });
    this.resizeSvc.onResize(currentSize?.id);
  }

  selectCollectionAsset(event: { asset: AssetUIModel; checked: boolean }) {
    this.collectionSelectionService.updateSelectedItems('collection', [{ _id: event.asset._id ?? event.asset.toString() }], false);
    this.filteredList.forEach((item) => {
      if (item._id === event.asset._id) item.checked = event.checked;
    });
    // Update the collection basket service based on the selection
    this.collectionSelectionService.updateCollectionBasket(event);
  }

  addRemoveFromBasket(asset: AssetUIModel) {
    if (asset['isAddedToBasket']) {
      this.assetBasketService.removeFromBasket(asset._id);
    } else {
      this.assetBasketService.addToBasket(asset._id);
    }
    asset['isAddedToBasket'] = !asset['isAddedToBasket'];

    this.collectionSelectionService.updateSelectedItems('collection', [{ _id: asset._id ?? asset.toString() }], false);
  }

  downloadAsset(asset: AssetUIModel) {
    const downloadData = this.downloadAssetsService.createDownloadData([asset]);
    this.dialog.open<AssetDownloadComponent, AssetDownloadData>(AssetDownloadComponent, {
      width: '100%',
      maxWidth: '70rem',
      height: '100%',
      maxHeight: '43rem',
      disableClose: true,
      data: { data: downloadData },
    });
  }

  editAssets(event: { editType: string; assets: AssetUIModel[]; domainMatching?: boolean }) {
    this.collectionAssetCommonService.editAssets(event);
  }

  applyFilters(data: AnyType) {
    this.collectionEventsService.collectionApplyFilterSubject$.next({ [this.collectionId]: data });
  }

  updateFormData(data: AnyType) {
    this.collectionEventsService.collectionUpdateFormDataSubject$.next({ data, assetSidebar: this.assetSidebar });
  }

  viewAssetTags() {
    this.tagsDialogRef = this.dialog.open<TagsDialogComponent, TagsDataConfig>(TagsDialogComponent, {
      maxWidth: '40rem',
      width: '50%',
      height: '62%',
      data: {
        selectedTags: this.selectedFilterObject[AssetMetadata.General.TAGS] ? this.selectedFilterObject[AssetMetadata.General.TAGS] : [],
        filters: this.filterObject.filters,
        callFrom: 'dialog',
      },
    });
    this.tagsDialogRef.disableClose = true;
    this.tagsDialogRef.afterClosed().subscribe(async (tags) => {
      if (tags?.length) {
        this.tagsObj = { tags: tags } as AnyType;
        this.applyFilters({ ...this.selectedFilterObject, ...this.tagsObj });
      } else {
        this.applyFilters({ ...this.selectedFilterObject });
      }
    });
  }

  createSplitList() {
    this.filteredSplitList = this.split(this.filteredList, this.size);
  }

  ngOnDestroy(): void {
    this.assetCommonService.selectedFilterDataSubject$.next({});
    this.subscriptions?.unsubscribe();
    this.resizeSubscription?.unsubscribe();
    this.assetBasketService.useAssetBasket();
  }
}
